import React, { useState, useEffect } from "react";
import { C, getPerms, canEditModule, canSeeModuleFinance } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, MeetingsTab, TransactionsTab,
  OperationalPlanTab, ReportsTab, useToast
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  getWomenPrograms, createWomenProgram,
  getWomenLessons, createWomenLesson,
  getWomenForums, createWomenForum,
  getWomenBeneficiaries, createWomenBeneficiary,
  getWomenNewMuslims, createWomenNewMuslim,
  getWomenPreachers, createWomenPreacher,
  getWomenContent, createWomenContentItem,
} from "./supabaseClient";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import {
  Heart, Users, UserPlus, BookOpen, GraduationCap, Calendar,
  CheckCircle, Clock, Plus, Search, Star, Target,
  FileText, TrendingUp, Award, MapPin,
  Mic, Sparkles, Megaphone, Activity,
  PieChart as PieChartIcon, BarChart3, Image as ImageIcon,
  MessageSquare, Video, Layers
} from "lucide-react";

/* ✦ محوّلات صفوف DB (snake_case) → camelCase الذي تقرأه الواجهة. */
function mapWProgramRow(r) {
  return {
    id: r.id,
    name: r.name,
    type: r.type || "",
    leader: r.leader || "—",
    schedule: r.schedule || "",
    beneficiaries: Number(r.beneficiaries) || 0,
    status: r.status || "planned",
    description: r.description || "",
  };
}

function mapWLessonRow(r) {
  return {
    id: r.id,
    title: r.title,
    kind: r.kind || "درس",
    speaker: r.speaker || "",
    date: r.lesson_date || "",
    location: r.location || "—",
    attendees: Number(r.attendees) || 0,
    status: r.status || "planned",
  };
}

function mapWForumRow(r) {
  return {
    id: r.id,
    title: r.title,
    theme: r.theme || "",
    date: r.forum_date || "",
    venue: r.venue || "—",
    attendees: Number(r.attendees) || 0,
    status: r.status || "planned",
  };
}

function mapWBeneficiaryRow(r) {
  return {
    id: r.id,
    name: r.name,
    nationality: r.nationality || "—",
    age: Number(r.age) || 0,
    program: r.program || "—",
    joinDate: r.join_date || "",
    attendance: Number(r.attendance) || 0,
  };
}

function mapWNewMuslimRow(r) {
  return {
    id: r.id,
    nameBefore: r.name_before || "",
    nameAfter: r.name_after,
    nationality: r.nationality || "",
    language: r.language || "—",
    islamDate: r.islam_date || "",
    preacher: r.preacher || "—",
    program: r.program || "—",
    followUp: r.follow_up || "نشطة",
    certNumber: r.cert_number || "",
    certDate: r.cert_date || "",
    certDelivered: !!r.cert_delivered,
  };
}

function mapWPreacherRow(r) {
  return {
    id: r.id,
    name: r.name,
    role: r.role || "fulltime",
    specialty: r.specialty || "",
    phone: r.phone || "",
    programs: Number(r.programs) || 0,
    beneficiaries: Number(r.beneficiaries) || 0,
    status: r.status || "نشطة",
  };
}

function mapWContentRow(r) {
  return {
    id: r.id,
    title: r.title,
    kind: r.kind || "رسالة",
    audience: r.audience || "",
    channel: r.channel || "",
    date: r.content_date || "",
    reach: Number(r.reach) || 0,
  };
}

/* ═════════════════════════════════════════════
   ✦ Women Page — القسم النسائي
   ═════════════════════════════════════════════ */
export default function WomenPage({ user }) {
  const [tab, setTab] = useState("overview");
  const [triggers, setTriggers] = useState({
    program: 0, lesson: 0, forum: 0, beneficiary: 0,
    newMuslim: 0, preacher: 0, content: 0
  });
  const perms = getPerms(user);
  const canEdit = canEditModule(perms, "women");

  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  useEffect(() => { getWomenPrograms().then(d => { setPrograms((d || []).map(mapWProgramRow)); setLoadingPrograms(false); }).catch(() => setLoadingPrograms(false)); }, []);

  const [lessons, setLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(true);
  useEffect(() => { getWomenLessons().then(d => { setLessons((d || []).map(mapWLessonRow)); setLoadingLessons(false); }).catch(() => setLoadingLessons(false)); }, []);

  const [forums, setForums] = useState([]);
  const [loadingForums, setLoadingForums] = useState(true);
  useEffect(() => { getWomenForums().then(d => { setForums((d || []).map(mapWForumRow)); setLoadingForums(false); }).catch(() => setLoadingForums(false)); }, []);

  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loadingBeneficiaries, setLoadingBeneficiaries] = useState(true);
  useEffect(() => { getWomenBeneficiaries().then(d => { setBeneficiaries((d || []).map(mapWBeneficiaryRow)); setLoadingBeneficiaries(false); }).catch(() => setLoadingBeneficiaries(false)); }, []);

  const [newMuslims, setNewMuslims] = useState([]);
  const [loadingNewMuslims, setLoadingNewMuslims] = useState(true);
  useEffect(() => { getWomenNewMuslims().then(d => { setNewMuslims((d || []).map(mapWNewMuslimRow)); setLoadingNewMuslims(false); }).catch(() => setLoadingNewMuslims(false)); }, []);

  const [preachers, setPreachers] = useState([]);
  const [loadingPreachers, setLoadingPreachers] = useState(true);
  useEffect(() => { getWomenPreachers().then(d => { setPreachers((d || []).map(mapWPreacherRow)); setLoadingPreachers(false); }).catch(() => setLoadingPreachers(false)); }, []);

  const [content, setContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(true);
  useEffect(() => { getWomenContent().then(d => { setContent((d || []).map(mapWContentRow)); setLoadingContent(false); }).catch(() => setLoadingContent(false)); }, []);

  const tabs = [
    { key: "overview",     label: "لوحة التحكم",       icon: Activity },
    { key: "programs",     label: "البرامج النسائية",  icon: BookOpen },
    { key: "lessons",      label: "الدروس والمحاضرات", icon: GraduationCap },
    { key: "forums",       label: "الملتقيات النسائية", icon: Sparkles },
    { key: "beneficiaries", label: "المستفيدات",        icon: Users },
    { key: "newMuslims",   label: "المسلمات الجديدات",  icon: Star },
    { key: "preachers",    label: "الداعيات والمتعاونات", icon: Award },
    { key: "content",      label: "المحتوى النسائي",   icon: Megaphone },
    { key: "reports",      label: "التقارير",          icon: FileText },
    { key: "meetings",     label: "الاجتماعات",         icon: Calendar },
    { key: "plan",         label: "الخطة التشغيلية",   icon: Target },
    { key: "finance",      label: "الشؤون المالية",    icon: FileText },
  ].filter(t => t.key !== "finance" || canSeeModuleFinance(perms, "women"));

  function fireQuick(targetTab, triggerKey) {
    setTab(targetTab);
    setTriggers(prev => ({ ...prev, [triggerKey]: prev[triggerKey] + 1 }));
  }

  const reportSources = [
    {
      label: "الدروس والمحاضرات", mode: "monthly", dateField: "date", rows: lessons,
      columns: [
        { key: "title", label: "العنوان" },
        { key: "kind", label: "النوع" },
        { key: "speaker", label: "المُحاضِرة" },
        { key: "date", label: "التاريخ" },
        { key: "attendees", label: "الحضور" },
      ],
    },
    {
      label: "الملتقيات النسائية", mode: "monthly", dateField: "date", rows: forums,
      columns: [
        { key: "title", label: "العنوان" },
        { key: "theme", label: "المحور" },
        { key: "date", label: "التاريخ" },
        { key: "venue", label: "المكان" },
        { key: "attendees", label: "الحضور" },
      ],
    },
    {
      label: "المستفيدات الجديدات", mode: "monthly", dateField: "joinDate", rows: beneficiaries,
      columns: [
        { key: "name", label: "الاسم" },
        { key: "nationality", label: "الجنسية" },
        { key: "program", label: "البرنامج" },
        { key: "joinDate", label: "تاريخ الالتحاق" },
      ],
    },
    {
      label: "المسلمات الجديدات", mode: "monthly", dateField: "islamDate", rows: newMuslims,
      columns: [
        { key: "nameAfter", label: "الاسم" },
        { key: "nationality", label: "الجنسية" },
        { key: "islamDate", label: "تاريخ الإسلام" },
        { key: "preacher", label: "الداعية" },
        { key: "followUp", label: "المتابعة" },
      ],
    },
    {
      label: "المحتوى النسائي", mode: "monthly", dateField: "date", rows: content,
      columns: [
        { key: "title", label: "العنوان" },
        { key: "kind", label: "النوع" },
        { key: "channel", label: "القناة" },
        { key: "date", label: "التاريخ" },
        { key: "reach", label: "الوصول" },
      ],
    },
    { label: "البرامج النسائية", mode: "cumulative", rows: programs },
    { label: "الداعيات والمتعاونات", mode: "cumulative", rows: preachers },
  ];

  return (
    <div>
      <ModuleHeader icon={Heart} title="القسم النسائي" subtitle="إدارة البرامج النسائية والدروس والملتقيات والمستفيدات والمسلمات الجديدات">
        <MonthlyReportButton moduleTitle="القسم النسائي" sources={reportSources} budget={760158} />
      </ModuleHeader>

      {canEdit && (
        <div style={wq.row}>
          <button style={wq.btn} onClick={() => fireQuick("programs", "program")}>
            <BookOpen size={16} color="#db2777" />
            <span>برنامج نسائي جديد</span>
          </button>
          <button style={wq.btn} onClick={() => fireQuick("lessons", "lesson")}>
            <GraduationCap size={16} color="#7c3aed" />
            <span>إضافة درس أو محاضرة</span>
          </button>
          <button style={wq.btn} onClick={() => fireQuick("newMuslims", "newMuslim")}>
            <Star size={16} color={C.accent} />
            <span>تسجيل مسلمة جديدة</span>
          </button>
          <button style={wq.btn} onClick={() => fireQuick("beneficiaries", "beneficiary")}>
            <UserPlus size={16} color={C.teal} />
            <span>إضافة مستفيدة</span>
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview"      && <OverviewTab programs={programs} lessons={lessons} forums={forums} newMuslims={newMuslims} preachers={preachers} />}
      {tab === "programs"      && <ProgramsTab     canEdit={canEdit} triggerOpen={triggers.program} programs={programs} setPrograms={setPrograms} loading={loadingPrograms} />}
      {tab === "lessons"       && <LessonsTab      canEdit={canEdit} triggerOpen={triggers.lesson} items={lessons} setItems={setLessons} loading={loadingLessons} />}
      {tab === "forums"        && <ForumsTab       canEdit={canEdit} triggerOpen={triggers.forum} items={forums} setItems={setForums} loading={loadingForums} />}
      {tab === "beneficiaries" && <BeneficiariesTab canEdit={canEdit} triggerOpen={triggers.beneficiary} data={beneficiaries} setData={setBeneficiaries} loading={loadingBeneficiaries} programs={programs} />}
      {tab === "newMuslims"    && <NewMuslimsTab   canEdit={canEdit} triggerOpen={triggers.newMuslim} items={newMuslims} setItems={setNewMuslims} loading={loadingNewMuslims} programs={programs} preachers={preachers} />}
      {tab === "preachers"     && <PreachersTab    canEdit={canEdit} triggerOpen={triggers.preacher} items={preachers} setItems={setPreachers} loading={loadingPreachers} />}
      {tab === "content"       && <ContentTab      canEdit={canEdit} triggerOpen={triggers.content} items={content} setItems={setContent} loading={loadingContent} />}
      {tab === "reports"       && <ReportsTab departmentName="القسم النسائي" canEdit={canEdit} />}
      {tab === "meetings"      && <MeetingsTab departmentName="القسم النسائي" module="women" canEdit={canEdit} />}
      {tab === "plan"          && <OperationalPlanTab departmentKey="women" canEdit={canEdit} />}
      {tab === "finance"       && canSeeModuleFinance(perms, "women") && <TransactionsTab budget={760158} module="women" />}
    </div>
  );
}

const wq = {
  row: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18 },
  btn: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "10px 18px", borderRadius: 10,
    background: C.white, color: C.primary, border: `1px solid ${C.border}`,
    fontFamily: "'Readex Pro', sans-serif", fontSize: 13, fontWeight: 600,
    cursor: "pointer", transition: "all 0.15s",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
};

/* ═════════════════════════════════════════════
   ✦ Demo Data
   ═════════════════════════════════════════════ */
const PROGRAM_TYPES = ["تربوي", "ملتقى", "دورة", "أسري", "فتيات", "مهتديات"];

const TYPE_COLORS = {
  "تربوي":   "#db2777",
  "ملتقى":   "#7c3aed",
  "دورة":    "#2563eb",
  "أسري":    "#ea580c",
  "فتيات":   C.teal,
  "مهتديات": C.accent,
};

const MONTHLY_BENEFICIARIES = [];
const UPCOMING_ACTIVITIES = [];
/* ─────────────────────────────────────────────
   ✦ Overview Tab — لوحة التحكم
   ───────────────────────────────────────────── */
function OverviewTab({ programs, lessons, forums, newMuslims, preachers }) {
  const totalBeneficiaries = programs.reduce((s, p) => s + p.beneficiaries, 0);
  const totalActivities    = lessons.length + forums.length + programs.length;

  const kpis = [
    { title: "البرامج",         value: programs.length,                     icon: BookOpen,       color: "#db2777" },
    { title: "الدروس",          value: lessons.filter(l => l.kind === "درس").length,    icon: GraduationCap, color: "#7c3aed" },
    { title: "المحاضرات",       value: lessons.filter(l => l.kind === "محاضرة").length, icon: Mic,           color: "#2563eb" },
    { title: "الملتقيات",        value: forums.length,                       icon: Sparkles,       color: "#ea580c" },
    { title: "المستفيدات",       value: totalBeneficiaries.toLocaleString(),     icon: Users,          color: C.teal },
    { title: "المسلمات الجدد",   value: newMuslims.length,                  icon: Star,           color: C.accent },
    { title: "الداعيات",          value: preachers.length,                   icon: Award,          color: C.success },
    { title: "المناشط",           value: totalActivities,                          icon: Activity,       color: C.primary },
  ];

  const benefByType = PROGRAM_TYPES.map(t => ({
    name: t,
    value: programs.filter(p => p.type === t).reduce((s, p) => s + p.beneficiaries, 0),
    color: TYPE_COLORS[t],
  })).filter(d => d.value > 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        {kpis.map((k, i) => (
          <KPICard key={i} title={k.title} value={k.value} icon={k.icon} color={k.color} />
        ))}
      </div>

      <div style={styles.chartsRow}>
        {/* Donut — توزيع المستفيدات حسب نوع البرنامج */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>توزيع المستفيدات حسب نوع البرنامج</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={benefByType} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}>
                {benefByType.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v.toLocaleString()} مستفيدة`]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={styles.legendGrid}>
            {benefByType.map((d, i) => (
              <div key={i} style={styles.legendItem}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Bar — المستفيدات شهرياً */}
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>المستفيدات شهرياً (12 شهراً)</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={MONTHLY_BENEFICIARIES} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" fontSize={10} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v.toLocaleString()} مستفيدة`]}
              />
              <Bar dataKey="value" name="المستفيدات" radius={[6, 6, 0, 0]} fill="#db2777" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Upcoming Activities */}
      <Card style={{ padding: 20, marginTop: 16 }}>
        <h4 style={styles.sectionTitle}><Calendar size={18} color="#ea580c" /> الأنشطة القادمة</h4>
        <div style={styles.upcomingGrid}>
          {UPCOMING_ACTIVITIES.map((a, i) => (
            <div key={i} style={styles.activityCard}>
              <div style={{ ...styles.activityDate, borderColor: a.color }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: C.primary, lineHeight: 1 }}>{a.day}</span>
                <span style={{ fontSize: 10, color: C.textLight }}>{a.month}</span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.primary }}>{a.title}</p>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight }}>{a.time} → {a.location}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Programs Tab — البرامج النسائية
   ───────────────────────────────────────────── */
const INITIAL_WPROG = { name: "", type: PROGRAM_TYPES[0], leader: "", schedule: "", beneficiaries: "", description: "", statusLabel: "مخطط" };
const WPROG_STATUS_LABEL_TO_KEY = { "نشط": "active", "مخطط": "planned", "منتهي": "completed" };
const PROG_STATUSES = [
  { key: "active", label: "نشط",    color: C.success },
  { key: "planned", label: "مخطط",   color: C.warning },
  { key: "completed", label: "منتهي", color: C.textLight },
];

function ProgramsTab({ canEdit, triggerOpen = 0, programs, setPrograms, loading }) {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_WPROG);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = programs.filter(p => {
    const matchSearch = p.name.includes(search) || p.leader.includes(search);
    const matchType = filterType === "all" || p.type === filterType;
    return matchSearch && matchType;
  });

  async function handleSave() {
    if (!form.name || !form.type) {
      showToast("الاسم والنوع مطلوبان", "error");
      return;
    }
    const payload = {
      name: form.name,
      type: form.type,
      leader: form.leader || "—",
      schedule: form.schedule || "",
      beneficiaries: Number(form.beneficiaries) || 0,
      status: WPROG_STATUS_LABEL_TO_KEY[form.statusLabel] || "planned",
      description: form.description,
    };
    try {
      const created = await createWomenProgram(payload);
      setPrograms(prev => [mapWProgramRow(created), ...prev]);
      setForm(INITIAL_WPROG);
      setShowAdd(false);
      showToast("تمت إضافة البرنامج");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ البرنامج", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي البرامج" value={programs.length} icon={BookOpen} color="#7c3aed" />
        <KPICard title="نشطة" value={programs.filter(p => p.status === "active").length} icon={CheckCircle} color={C.success} />
        <KPICard title="مخططة" value={programs.filter(p => p.status === "planned").length} icon={Clock} color={C.warning} />
        <KPICard title="إجمالي المستفيدات" value={programs.reduce((s, p) => s + p.beneficiaries, 0).toLocaleString()} icon={Users} color="#db2777" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو القائدة..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              {PROGRAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">برنامج جديد</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "اسم البرنامج", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "type", label: "النوع", width: "100px", render: v => (
              <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: `${TYPE_COLORS[v]}15`, color: TYPE_COLORS[v] }}>{v}</span>
            ) },
            { key: "leader", label: "القائدة", width: "150px" },
            { key: "schedule", label: "الموعد", width: "150px" },
            { key: "beneficiaries", label: "المستفيدات", width: "100px", render: v => v.toLocaleString() },
            { key: "status", label: "الحالة", width: "100px", render: v => <StatusBadge status={v} statuses={PROG_STATUSES} /> },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={selected.name} onClose={() => setSelected(null)} width={550}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>النوع:</strong> {selected.type}</div>
            <div style={styles.detailRow}><strong>القائدة:</strong> {selected.leader}</div>
            <div style={styles.detailRow}><strong>الموعد:</strong> {selected.schedule}</div>
            <div style={styles.detailRow}><strong>المستفيدات:</strong> {selected.beneficiaries.toLocaleString()}</div>
            <div style={styles.detailRow}><strong>الوصف:</strong> {selected.description}</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> <StatusBadge status={selected.status} statuses={PROG_STATUSES} /></div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة برنامج نسائي" onClose={() => { setShowAdd(false); setForm(INITIAL_WPROG); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="اسم البرنامج" required placeholder="مثال: دورة العقيدة الميسرة" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={PROGRAM_TYPES} required value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="القائدة/المسؤولة" placeholder="اسم القائدة" value={form.leader} onChange={v => setForm({ ...form, leader: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الموعد" placeholder="الأحد والثلاثاء 4-6م" value={form.schedule} onChange={v => setForm({ ...form, schedule: v })} />
              <Field label="عدد المستفيدات" type="number" placeholder="0" value={form.beneficiaries} onChange={v => setForm({ ...form, beneficiaries: v })} />
              <Field label="الحالة" type="select" options={["نشط", "مخطط", "منتهي"]} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <Field label="الوصف" type="textarea" placeholder="وصف تفصيلي للبرنامج..." rows={3} value={form.description} onChange={v => setForm({ ...form, description: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={BookOpen} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_WPROG); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Lessons & Lectures Tab — الدروس والمحاضرات
   ───────────────────────────────────────────── */
const INITIAL_LESSON = { title: "", kind: "درس", speaker: "", date: "", location: "", attendees: "", statusLabel: "مخطط" };
const LESSON_STATUS_LABEL_TO_KEY = { "منعقد": "completed", "مخطط": "planned" };
const LESSON_STATUSES = [
  { key: "completed", label: "منعقد", color: C.success },
  { key: "planned",   label: "مخطط",  color: C.warning },
];

function LessonsTab({ canEdit, triggerOpen = 0, items, setItems, loading }) {
  const [search, setSearch] = useState("");
  const [filterKind, setFilterKind] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_LESSON);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = items.filter(l => {
    const matchSearch = l.title.includes(search) || l.speaker.includes(search);
    const matchKind = filterKind === "all" || l.kind === filterKind;
    return matchSearch && matchKind;
  });

  async function handleSave() {
    if (!form.title || !form.speaker) {
      showToast("العنوان والمحاضر/المدرس مطلوبان", "error");
      return;
    }
    const payload = {
      title: form.title,
      kind: form.kind,
      speaker: form.speaker,
      lesson_date: form.date || "",
      location: form.location || "—",
      attendees: Number(form.attendees) || 0,
      status: LESSON_STATUS_LABEL_TO_KEY[form.statusLabel] || "planned",
    };
    try {
      const created = await createWomenLesson(payload);
      setItems(prev => [mapWLessonRow(created), ...prev]);
      setForm(INITIAL_LESSON);
      setShowAdd(false);
      showToast("تمت الإضافة");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ الدرس", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="الإجمالي" value={items.length} icon={GraduationCap} color="#7c3aed" />
        <KPICard title="الدروس" value={items.filter(l => l.kind === "درس").length} icon={BookOpen} color={C.teal} />
        <KPICard title="المحاضرات" value={items.filter(l => l.kind === "محاضرة").length} icon={Mic} color="#2563eb" />
        <KPICard title="الحضور التراكمي" value={items.reduce((s, l) => s + l.attendees, 0).toLocaleString()} icon={Users} color="#db2777" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالعنوان أو المحاضر..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterKind} onChange={e => setFilterKind(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              <option value="درس">درس</option>
              <option value="محاضرة">محاضرة</option>
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "title",   label: "العنوان", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "kind",    label: "النوع", width: "90px", render: v => (
              <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: v === "درس" ? `${C.teal}15` : "#2563eb15", color: v === "درس" ? C.teal : "#2563eb" }}>{v}</span>
            ) },
            { key: "speaker", label: "المحاضر/المدرس", width: "160px" },
            { key: "date",    label: "التاريخ", width: "110px" },
            { key: "location", label: "الموقع", width: "200px" },
            { key: "attendees", label: "الحضور", width: "80px", render: v => v.toLocaleString() },
            { key: "status",  label: "الحالة", width: "90px", render: v => <StatusBadge status={v} statuses={LESSON_STATUSES} /> },
          ]}
          data={filtered}
        />
      </Card>

      {showAdd && (
        <Modal title="إضافة درس أو محاضرة" onClose={() => { setShowAdd(false); setForm(INITIAL_LESSON); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="مثال: درس فقه الطهارة للمرأة" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={["درس", "محاضرة"]} value={form.kind} onChange={v => setForm({ ...form, kind: v })} />
              <Field label="المحاضر/المدرس" required placeholder="اسم المحاضر" value={form.speaker} onChange={v => setForm({ ...form, speaker: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="الموقع" placeholder="مصلى النساء — قاعة الجمعية..." value={form.location} onChange={v => setForm({ ...form, location: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="عدد الحضور" type="number" placeholder="0" value={form.attendees} onChange={v => setForm({ ...form, attendees: v })} />
              <Field label="الحالة" type="select" options={["مخطط", "منعقد"]} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={GraduationCap} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_LESSON); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Forums Tab — الملتقيات النسائية
   ───────────────────────────────────────────── */
const INITIAL_FORUM = { title: "", theme: "", date: "", venue: "", attendees: "", statusLabel: "مخطط" };
const FORUM_STATUS_LABEL_TO_KEY = { "منعقد": "completed", "مخطط": "planned" };
const FORUM_STATUSES = [
  { key: "completed", label: "منعقد", color: C.success },
  { key: "planned",   label: "مخطط",  color: C.warning },
];

function ForumsTab({ canEdit, triggerOpen = 0, items, setItems, loading }) {
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_FORUM);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = items.filter(f => f.title.includes(search) || f.theme.includes(search));

  async function handleSave() {
    if (!form.title) {
      showToast("العنوان مطلوب", "error");
      return;
    }
    const payload = {
      title: form.title,
      theme: form.theme,
      forum_date: form.date || "",
      venue: form.venue || "—",
      attendees: Number(form.attendees) || 0,
      status: FORUM_STATUS_LABEL_TO_KEY[form.statusLabel] || "planned",
    };
    try {
      const created = await createWomenForum(payload);
      setItems(prev => [mapWForumRow(created), ...prev]);
      setForm(INITIAL_FORUM);
      setShowAdd(false);
      showToast("تمت إضافة الملتقى");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ الملتقى", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الملتقيات" value={items.length} icon={Sparkles} color="#ea580c" />
        <KPICard title="منعقدة" value={items.filter(f => f.status === "completed").length} icon={CheckCircle} color={C.success} />
        <KPICard title="مخططة" value={items.filter(f => f.status === "planned").length} icon={Clock} color={C.warning} />
        <KPICard title="إجمالي الحاضرات" value={items.reduce((s, f) => s + f.attendees, 0).toLocaleString()} icon={Users} color="#db2777" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالعنوان أو الموضوع..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">ملتقى جديد</Btn>}
        </div>
      </Card>

      <div style={styles.forumGrid}>
        {filtered.map(f => (
          <Card key={f.id} style={styles.forumCard} onClick={() => setSelected(f)}>
            <div style={styles.forumHeader}>
              <div style={styles.forumIcon}>
                <Sparkles size={20} color="#ea580c" />
              </div>
              <StatusBadge status={f.status} statuses={FORUM_STATUSES} />
            </div>
            <h4 style={{ margin: "12px 0 4px", fontSize: 15, fontWeight: 700, color: C.primary }}>{f.title}</h4>
            <p style={{ margin: 0, fontSize: 12, color: "#ea580c", fontWeight: 500 }}>{f.theme}</p>
            <div style={styles.forumStats}>
              <span><Calendar size={13} /> {f.date}</span>
              <span><MapPin size={13} /> {f.venue}</span>
            </div>
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textLight }}>
              <Users size={13} /> {f.attendees.toLocaleString()} حاضرة
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={selected.title} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={styles.detailRow}><strong>العنوان:</strong> {selected.title}</div>
            <div style={styles.detailRow}><strong>الموضوع:</strong> {selected.theme}</div>
            <div style={styles.detailRow}><strong>التاريخ:</strong> {selected.date}</div>
            <div style={styles.detailRow}><strong>المكان:</strong> {selected.venue}</div>
            <div style={styles.detailRow}><strong>عدد الحاضرات:</strong> {selected.attendees.toLocaleString()}</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> <StatusBadge status={selected.status} statuses={FORUM_STATUSES} /></div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة ملتقى نسائي" onClose={() => { setShowAdd(false); setForm(INITIAL_FORUM); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="مثال: ملتقى الأم المربية" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <Field label="الموضوع" placeholder="موضوع الملتقى" value={form.theme} onChange={v => setForm({ ...form, theme: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="المكان" placeholder="قاعة الجمعية..." value={form.venue} onChange={v => setForm({ ...form, venue: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="عدد الحاضرات" type="number" placeholder="0" value={form.attendees} onChange={v => setForm({ ...form, attendees: v })} />
              <Field label="الحالة" type="select" options={["مخطط", "منعقد"]} value={form.statusLabel} onChange={v => setForm({ ...form, statusLabel: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Sparkles} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_FORUM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Beneficiaries Tab — المستفيدات
   ───────────────────────────────────────────── */
const INITIAL_WBEN = { name: "", nationality: "", age: "", phone: "", program: "", registrationDate: "" };

function BeneficiariesTab({ canEdit, triggerOpen = 0, data, setData, loading, programs }) {
  const [search, setSearch] = useState("");
  const [filterNat, setFilterNat] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_WBEN);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = data.filter(b => {
    const matchSearch = b.name.includes(search) || b.program.includes(search);
    const matchNat = filterNat === "all" || (filterNat === "سعودية" ? b.nationality === "سعودية" : b.nationality !== "سعودية");
    return matchSearch && matchNat;
  });

  async function handleSave() {
    if (!form.name) {
      showToast("الاسم مطلوب", "error");
      return;
    }
    const payload = {
      name: form.name,
      nationality: form.nationality || "—",
      age: Number(form.age) || 0,
      program: form.program || "—",
      join_date: form.registrationDate || new Date().toISOString().slice(0, 10),
      attendance: 100,
    };
    try {
      const created = await createWomenBeneficiary(payload);
      setData(prev => [mapWBeneficiaryRow(created), ...prev]);
      setForm(INITIAL_WBEN);
      setShowAdd(false);
      showToast("تمت إضافة المستفيدة");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ المستفيدة", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المستفيدات" value={data.length} icon={Users} color="#db2777" />
        <KPICard title="سعوديات" value={data.filter(b => b.nationality === "سعودية").length} icon={Star} color={C.accent} />
        <KPICard title="غير سعوديات" value={data.filter(b => b.nationality !== "سعودية").length} icon={MapPin} color={C.teal} />
        <KPICard title="متوسط الحضور" value={`${data.length ? Math.round(data.reduce((s, b) => s + b.attendance, 0) / data.length) : 0}%`} icon={Activity} color={C.success} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو البرنامج..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterNat} onChange={e => setFilterNat(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الجنسيات</option>
              <option value="سعودية">سعوديات</option>
              <option value="other">غير سعوديات</option>
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة مستفيدة</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "الاسم", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "nationality", label: "الجنسية", width: "110px" },
            { key: "age", label: "العمر", width: "70px" },
            { key: "program", label: "البرنامج", width: "220px" },
            { key: "joinDate", label: "التسجيل", width: "110px" },
            { key: "attendance", label: "الحضور", width: "90px", render: v => (
              <span style={{ color: v >= 80 ? C.success : v >= 60 ? C.warning : C.danger, fontWeight: 600 }}>{v}%</span>
            )},
          ]}
          data={filtered}
        />
      </Card>

      {showAdd && (
        <Modal title="إضافة مستفيدة" onClose={() => { setShowAdd(false); setForm(INITIAL_WBEN); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الاسم الكامل" required placeholder="اسم المستفيدة" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الجنسية" type="select" options={["سعودية","مصرية","سورية","يمنية","أردنية","فلبينية","إندونيسية","كازاخستانية","أوزبكية","أخرى"]} value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="العمر" type="number" placeholder="0" value={form.age} onChange={v => setForm({ ...form, age: v })} />
              <Field label="الهاتف" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="البرنامج" type="select" options={programs.map(p => p.name)} value={form.program} onChange={v => setForm({ ...form, program: v })} />
              <Field label="تاريخ التسجيل" type="date" value={form.registrationDate} onChange={v => setForm({ ...form, registrationDate: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={UserPlus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_WBEN); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ New Muslims Tab — المسلمات الجديدات
   ───────────────────────────────────────────── */
const FOLLOW_UP_STATUSES = ["نشطة", "متقطعة", "متوقفة"];

const INITIAL_NM = {
  nameBefore: "", nameAfter: "", nationality: "", language: "",
  islamDate: "", preacher: "", program: "", followUp: "نشطة",
  certNumber: "", certDate: "", certDelivered: false,
};

function NewMuslimsTab({ canEdit, triggerOpen = 0, items, setItems, loading, programs, preachers }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_NM);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = items.filter(m => {
    const matchSearch = m.nameAfter.includes(search) || m.nameBefore.toLowerCase().includes(search.toLowerCase()) || m.nationality.includes(search);
    const matchStatus = filterStatus === "all" || m.followUp === filterStatus;
    return matchSearch && matchStatus;
  });

  async function handleSave() {
    if (!form.nameAfter || !form.nationality) {
      showToast("الاسم بعد الإسلام والجنسية مطلوبان", "error");
      return;
    }
    const payload = {
      name_before: form.nameBefore,
      name_after: form.nameAfter,
      nationality: form.nationality,
      language: form.language || "—",
      islam_date: form.islamDate || "",
      preacher: form.preacher || "—",
      program: form.program || "—",
      follow_up: form.followUp,
      cert_number: form.certNumber,
      cert_date: form.certDate,
      cert_delivered: !!form.certDelivered,
    };
    try {
      const created = await createWomenNewMuslim(payload);
      setItems(prev => [mapWNewMuslimRow(created), ...prev]);
      setForm(INITIAL_NM);
      setShowAdd(false);
      showToast("تم تسجيل المسلمة الجديدة");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ البيانات", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي المهتديات" value={items.length} icon={Star} color={C.accent} />
        <KPICard title="نشطة المتابعة" value={items.filter(m => m.followUp === "نشطة").length} icon={CheckCircle} color={C.success} />
        <KPICard title="شهادات مسلَّمة" value={items.filter(m => m.certDelivered).length} icon={Award} color={C.teal} />
        <KPICard title="شهادات معلقة" value={items.filter(m => !m.certDelivered).length} icon={Clock} color={C.warning} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الجنسية..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {FOLLOW_UP_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">تسجيل مسلمة جديدة</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "nameAfter",   label: "الاسم بعد الإسلام", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "nameBefore",  label: "الاسم قبل الإسلام", width: "160px" },
            { key: "nationality", label: "الجنسية", width: "110px" },
            { key: "language",    label: "اللغة", width: "100px" },
            { key: "islamDate",   label: "تاريخ الإسلام", width: "110px" },
            { key: "preacher",    label: "الداعية", width: "150px" },
            { key: "followUp",    label: "المتابعة", width: "100px", render: v => (
              <span style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: v === "نشطة" ? `${C.success}15` : v === "متقطعة" ? `${C.warning}15` : `${C.danger}15`,
                color: v === "نشطة" ? C.success : v === "متقطعة" ? C.warning : C.danger,
              }}>{v}</span>
            )},
            { key: "certDelivered", label: "الشهادة", width: "90px", render: (v, row) => (
              row.certNumber
                ? <span style={{ color: v ? C.success : C.warning, fontSize: 12, fontWeight: 600 }}>{v ? "مسلَّمة" : "جاهزة"}</span>
                : <span style={{ color: C.textLight, fontSize: 12 }}>—</span>
            )},
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {selected && (
        <Modal title={`بطاقة المهتدية — ${selected.nameAfter}`} onClose={() => setSelected(null)} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={styles.detailRow}><strong>الاسم بعد الإسلام:</strong> {selected.nameAfter}</div>
            <div style={styles.detailRow}><strong>الاسم قبل الإسلام:</strong> {selected.nameBefore || "—"}</div>
            <div style={styles.detailRow}><strong>الجنسية:</strong> {selected.nationality}</div>
            <div style={styles.detailRow}><strong>اللغة:</strong> {selected.language}</div>
            <div style={styles.detailRow}><strong>تاريخ الإسلام:</strong> {selected.islamDate}</div>
            <div style={styles.detailRow}><strong>الداعية المسؤولة:</strong> {selected.preacher}</div>
            <div style={styles.detailRow}><strong>البرنامج التعليمي:</strong> {selected.program}</div>
            <div style={styles.detailRow}><strong>حالة المتابعة:</strong> {selected.followUp}</div>
            <div style={styles.sectionDivider}>الشهادة</div>
            <div style={styles.detailRow}><strong>رقم الشهادة:</strong> {selected.certNumber || "—"}</div>
            <div style={styles.detailRow}><strong>تاريخ الإصدار:</strong> {selected.certDate || "—"}</div>
            <div style={styles.detailRow}><strong>التسليم:</strong> {selected.certDelivered ? "تم التسليم" : "لم يتم التسليم بعد"}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="تسجيل مسلمة جديدة" onClose={() => { setShowAdd(false); setForm(INITIAL_NM); }} width={720}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="الاسم قبل الإسلام" placeholder="مثال: Maria Santos" value={form.nameBefore} onChange={v => setForm({ ...form, nameBefore: v })} />
              <Field label="الاسم بعد الإسلام" required placeholder="مثال: مريم سانتوس" value={form.nameAfter} onChange={v => setForm({ ...form, nameAfter: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="الجنسية" required type="select" options={["فلبينية","إندونيسية","أوزبكية","كازاخستانية","سريلانكية","تايلندية","إثيوبية","نيجيرية","أخرى"]} value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="اللغة" type="select" options={["إنجليزية","إندونيسية","روسية","تاغالوغ","تايلندية","أمهرية","فرنسية","أخرى"]} value={form.language} onChange={v => setForm({ ...form, language: v })} />
              <Field label="تاريخ الإسلام" type="date" value={form.islamDate} onChange={v => setForm({ ...form, islamDate: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="الداعية المسؤولة" type="select" options={preachers.map(p => p.name)} value={form.preacher} onChange={v => setForm({ ...form, preacher: v })} />
              <Field label="البرنامج التعليمي" type="select" options={programs.map(p => p.name)} value={form.program} onChange={v => setForm({ ...form, program: v })} />
              <Field label="حالة المتابعة" type="select" options={FOLLOW_UP_STATUSES} value={form.followUp} onChange={v => setForm({ ...form, followUp: v })} />
            </div>

            <div>
              <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: C.teal, borderBottom: `2px solid ${C.teal}30`, paddingBottom: 6 }}>شهادة إعلان الإسلام</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="رقم الشهادة" placeholder="BNR-W-2025-000" value={form.certNumber} onChange={v => setForm({ ...form, certNumber: v })} />
                <Field label="تاريخ الإصدار" type="date" value={form.certDate} onChange={v => setForm({ ...form, certDate: v })} />
                <Field label="حالة التسليم" type="select" options={["لم تسلَّم", "مسلَّمة"]} value={form.certDelivered ? "مسلَّمة" : "لم تسلَّم"} onChange={v => setForm({ ...form, certDelivered: v === "مسلَّمة" })} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Star} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_NM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Preachers Tab — الداعيات والمتعاونات
   ───────────────────────────────────────────── */
const INITIAL_PR = { name: "", role: "fulltime", specialty: "", phone: "", programs: "", beneficiaries: "", status: "نشطة" };
const PR_SPECIALTIES = ["تحفيظ قرآن", "تجويد", "فقه", "عقيدة", "تربية أسرية", "دعوة عامة", "تدريب مهني"];

function PreachersTab({ canEdit, triggerOpen = 0, items, setItems, loading }) {
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INITIAL_PR);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = items.filter(p => {
    const matchSearch = p.name.includes(search) || p.specialty.includes(search);
    const matchRole = filterRole === "all" || p.role === filterRole;
    return matchSearch && matchRole;
  });

  async function handleSave() {
    if (!form.name || !form.specialty) {
      showToast("الاسم والتخصص مطلوبان", "error");
      return;
    }
    const payload = {
      name: form.name,
      role: form.role,
      specialty: form.specialty,
      phone: form.phone,
      programs: Number(form.programs) || 0,
      beneficiaries: Number(form.beneficiaries) || 0,
      status: form.status,
    };
    try {
      const created = await createWomenPreacher(payload);
      setItems(prev => [mapWPreacherRow(created), ...prev]);
      setForm(INITIAL_PR);
      setShowAdd(false);
      showToast("تمت إضافة الداعية");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ الداعية", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الداعيات" value={items.length} icon={Award} color={C.teal} />
        <KPICard title="متفرغات" value={items.filter(p => p.role === "fulltime").length} icon={CheckCircle} color={C.success} />
        <KPICard title="متعاونات" value={items.filter(p => p.role === "parttime").length} icon={Clock} color={C.warning} />
        <KPICard title="إجمالي المستفيدات" value={items.reduce((s, p) => s + p.beneficiaries, 0).toLocaleString()} icon={Users} color="#db2777" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو التخصص..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع التعاقدات</option>
              <option value="fulltime">متفرغة</option>
              <option value="parttime">متعاونة</option>
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة داعية</Btn>}
          </div>
        </div>
      </Card>

      <div style={styles.preacherGrid}>
        {filtered.map(p => (
          <Card key={p.id} style={styles.preacherCard} onClick={() => setSelected(p)}>
            <div style={styles.preacherTop}>
              <div style={styles.preacherAvatar}>{p.name.charAt(p.name.indexOf(".") + 2) || "?"}</div>
              <span style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
                background: p.role === "fulltime" ? `${C.success}15` : `${C.warning}15`,
                color: p.role === "fulltime" ? C.success : C.warning,
              }}>{p.role === "fulltime" ? "متفرغة" : "متعاونة"}</span>
            </div>
            <h4 style={{ margin: "10px 0 2px", fontSize: 15, fontWeight: 700, color: C.primary }}>{p.name}</h4>
            <p style={{ margin: 0, fontSize: 13, color: "#db2777", fontWeight: 500 }}>{p.specialty}</p>
            <div style={styles.preacherStats}>
              <span><BookOpen size={12} /> {p.programs} برامج</span>
              <span><Users size={12} /> {p.beneficiaries} مستفيدة</span>
            </div>
          </Card>
        ))}
      </div>

      {selected && (
        <Modal title={`الداعية — ${selected.name}`} onClose={() => setSelected(null)} width={500}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={styles.detailRow}><strong>الاسم:</strong> {selected.name}</div>
            <div style={styles.detailRow}><strong>نوع التعاقد:</strong> {selected.role === "fulltime" ? "متفرغة" : "متعاونة"}</div>
            <div style={styles.detailRow}><strong>التخصص:</strong> {selected.specialty}</div>
            <div style={styles.detailRow}><strong>الجوال:</strong> <span dir="ltr">{selected.phone}</span></div>
            <div style={styles.detailRow}><strong>عدد البرامج:</strong> {selected.programs}</div>
            <div style={styles.detailRow}><strong>عدد المستفيدات:</strong> {selected.beneficiaries.toLocaleString()}</div>
            <div style={styles.detailRow}><strong>الحالة:</strong> {selected.status}</div>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal title="إضافة داعية / متعاونة" onClose={() => { setShowAdd(false); setForm(INITIAL_PR); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الاسم الكامل" required placeholder="اسم الداعية" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="نوع التعاقد" type="select" options={["متفرغة", "متعاونة"]} value={form.role === "fulltime" ? "متفرغة" : "متعاونة"} onChange={v => setForm({ ...form, role: v === "متفرغة" ? "fulltime" : "parttime" })} />
              <Field label="التخصص" required type="select" options={PR_SPECIALTIES} value={form.specialty} onChange={v => setForm({ ...form, specialty: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="الجوال" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="عدد البرامج" type="number" placeholder="0" value={form.programs} onChange={v => setForm({ ...form, programs: v })} />
              <Field label="عدد المستفيدات" type="number" placeholder="0" value={form.beneficiaries} onChange={v => setForm({ ...form, beneficiaries: v })} />
            </div>
            <Field label="الحالة" type="select" options={["نشطة", "متوقفة"]} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Award} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_PR); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Content Tab — المحتوى النسائي
   ───────────────────────────────────────────── */
const CONTENT_KINDS = ["رسالة", "تصميم", "مقطع", "منشور"];
const CONTENT_CHANNELS = ["واتساب", "إنستغرام", "تويتر", "تيك توك", "يوتيوب", "سناب شات", "تلغرام"];
const CONTENT_AUDIENCES = ["المستفيدات", "الفتيات", "الأمهات", "المهتديات", "عموم النساء"];

const INITIAL_CN = { title: "", kind: "رسالة", audience: "المستفيدات", channel: "واتساب", date: "", reach: "" };

const CONTENT_ICON = (k) => {
  if (k === "رسالة")  return MessageSquare;
  if (k === "تصميم")  return ImageIcon;
  if (k === "مقطع")   return Video;
  return Layers;
};

function ContentTab({ canEdit, triggerOpen = 0, items, setItems, loading }) {
  const [search, setSearch] = useState("");
  const [filterKind, setFilterKind] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_CN);
  const [showToast, ToastView] = useToast();

  useEffect(() => { if (triggerOpen > 0) setShowAdd(true); /* eslint-disable-next-line */ }, [triggerOpen]);

  const filtered = items.filter(c => {
    const matchSearch = c.title.includes(search) || c.audience.includes(search);
    const matchKind = filterKind === "all" || c.kind === filterKind;
    return matchSearch && matchKind;
  });

  async function handleSave() {
    if (!form.title) {
      showToast("العنوان مطلوب", "error");
      return;
    }
    const payload = {
      title: form.title,
      kind: form.kind,
      audience: form.audience,
      channel: form.channel,
      content_date: form.date || "",
      reach: Number(form.reach) || 0,
    };
    try {
      const created = await createWomenContentItem(payload);
      setItems(prev => [mapWContentRow(created), ...prev]);
      setForm(INITIAL_CN);
      setShowAdd(false);
      showToast("تمت إضافة المحتوى");
    } catch (e) {
      showToast(e.message || "تعذّر حفظ المحتوى", "error");
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.kpiRow}>
        {CONTENT_KINDS.map(k => {
          const Icon = CONTENT_ICON(k);
          return <KPICard key={k} title={k} value={items.filter(c => c.kind === k).length} icon={Icon} color={
            k === "رسالة" ? "#db2777" : k === "تصميم" ? "#7c3aed" : k === "مقطع" ? "#ea580c" : C.teal
          } />;
        })}
        <KPICard title="إجمالي الوصول" value={items.reduce((s, c) => s + c.reach, 0).toLocaleString()} icon={TrendingUp} color={C.success} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالعنوان أو الجمهور..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterKind} onChange={e => setFilterKind(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              {CONTENT_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة محتوى</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "title", label: "العنوان", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "kind",  label: "النوع", width: "90px", render: v => (
              <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: "#db277715", color: "#db2777" }}>{v}</span>
            )},
            { key: "audience", label: "الجمهور", width: "130px" },
            { key: "channel",  label: "القناة", width: "110px" },
            { key: "date",     label: "التاريخ", width: "110px" },
            { key: "reach",    label: "الوصول", width: "100px", render: v => v.toLocaleString() },
          ]}
          data={filtered}
        />
      </Card>

      {showAdd && (
        <Modal title="إضافة محتوى نسائي" onClose={() => { setShowAdd(false); setForm(INITIAL_CN); }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="العنوان" required placeholder="مثال: رسالة فضل قيام الليل" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Field label="النوع" type="select" options={CONTENT_KINDS} value={form.kind} onChange={v => setForm({ ...form, kind: v })} />
              <Field label="الجمهور المستهدف" type="select" options={CONTENT_AUDIENCES} value={form.audience} onChange={v => setForm({ ...form, audience: v })} />
              <Field label="قناة النشر" type="select" options={CONTENT_CHANNELS} value={form.channel} onChange={v => setForm({ ...form, channel: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="تاريخ النشر" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="الوصول المتوقع" type="number" placeholder="0" value={form.reach} onChange={v => setForm({ ...form, reach: v })} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Megaphone} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_CN); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16,
  },
  sectionTitle: {
    display: "flex", alignItems: "center", gap: 8,
    fontSize: 16, fontWeight: 700, color: C.primary, margin: "0 0 14px",
  },
  toolbar: {
    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  filterSelect: {
    padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer",
  },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  sectionDivider: {
    marginTop: 8, paddingBottom: 4,
    borderBottom: `2px solid ${C.teal}30`,
    fontSize: 13, fontWeight: 700, color: C.teal,
  },

  /* Charts */
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
    gap: 20, marginBottom: 20,
  },
  chartCard: { padding: 20 },
  chartHeader: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", marginBottom: 16,
  },
  chartTitle: { fontSize: 15, fontWeight: 700, color: C.primary },
  legendGrid: {
    display: "flex", flexWrap: "wrap", gap: 12,
    justifyContent: "center", marginTop: 8,
  },
  legendItem: { display: "flex", alignItems: "center", gap: 4 },

  /* Upcoming activities */
  upcomingGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12,
  },
  activityCard: {
    display: "flex", alignItems: "center", gap: 12, padding: 10,
    background: C.bg, borderRadius: 10,
  },
  activityDate: {
    width: 48, height: 48, borderRadius: 10, background: C.white,
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", border: "2px solid", flexShrink: 0,
  },

  /* Forums */
  forumGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16,
  },
  forumCard: { padding: 20, cursor: "pointer" },
  forumHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  forumIcon: {
    width: 44, height: 44, borderRadius: 10, display: "flex",
    alignItems: "center", justifyContent: "center", background: "#ea580c15",
  },
  forumStats: {
    display: "flex", gap: 16, marginTop: 10, fontSize: 12, color: C.textLight,
  },

  /* Preachers grid */
  preacherGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16,
  },
  preacherCard: { padding: 18, cursor: "pointer" },
  preacherTop: {
    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  },
  preacherAvatar: {
    width: 46, height: 46, borderRadius: 12,
    background: `linear-gradient(135deg, #db2777, #f472b6)`,
    color: C.white, display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 18,
  },
  preacherStats: {
    display: "flex", gap: 16, marginTop: 10, paddingTop: 10,
    borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textLight,
  },
};
